/**
 * Response decoder utility for handling encoded API responses.
 * 
 * This module decodes responses that have been encoded by the backend
 * to hide data from Network tab inspection while preserving UI functionality.
 */

import pako from 'pako'; // You'll need to install: npm install pako
import { authFetch } from './authFetch';

/**
 * Decode encoded API responses
 * @param {Object} response - The response from the API
 * @returns {Object} - The decoded response data
 */
export function decodeApiResponse(response) {
    try {
        // Check if response is encoded
        if (response && response.success && response.payload && response.encoding === "gzip+base64") {
            console.log('🔓 Decoding API response for UI display');
            
            // Decode base64
            const compressed = atob(response.payload);
            
            // Convert to Uint8Array for decompression
            const compressedArray = new Uint8Array(compressed.length);
            for (let i = 0; i < compressed.length; i++) {
                compressedArray[i] = compressed.charCodeAt(i);
            }
            
            // Decompress using pako
            const decompressed = pako.inflate(compressedArray, { to: 'string' });
            
            // Parse JSON
            const decodedData = JSON.parse(decompressed);
            
            console.log('✅ API response decoded successfully');
            return decodedData;
        }
        
        // Return original response if not encoded
        return response;
        
    } catch (error) {
        console.error('❌ Failed to decode API response:', error);
        console.warn('⚠️ Returning original response');
        return response; // Return original on error
    }
}

/**
 * Wrapper for fetch that automatically decodes responses
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - The decoded response data
 */
export async function fetchAndDecode(url, options = {}) {
    try {
        console.log(`🌐 Fetching: ${url}`);

        const response = await authFetch(url, options);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        const decodedData = decodeApiResponse(data);
        
        console.log(`✅ Fetch and decode completed: ${url}`);
        return decodedData;
        
    } catch (error) {
        console.error(`❌ Fetch and decode error for ${url}:`, error);
        throw error;
    }
}

/**
 * Enhanced fetch wrapper with automatic decoding and error handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - The decoded response data
 */
export async function apiRequest(url, options = {}) {
    try {
        // Set default headers
        const defaultHeaders = {
            'Content-Type': 'application/json',
            ...options.headers
        };
        
        const requestOptions = {
            ...options,
            headers: defaultHeaders
        };
        
        console.log(`🚀 API Request: ${options.method || 'GET'} ${url}`);
        
        const response = await authFetch(url, requestOptions);
        
        // Handle different status codes. Error responses carry their status
        // and parsed detail body (allowance 429s include used/limit/resets/
        // upgrade_to) so callers can render the right limit UI.
        if (!response.ok) {
            let detail = null;
            try { detail = (await response.json())?.detail ?? null; } catch { /* non-JSON */ }
            const messages = {
                401: 'Unauthorized - Please check your API key',
                403: 'Forbidden - Access denied',
                404: 'Not found - Endpoint does not exist',
            };
            const msg = messages[response.status]
                || (response.status >= 500 ? 'Server error - Please try again later'
                    : `HTTP ${response.status}: ${response.statusText}`);
            const error = new Error(msg);
            error.status = response.status;
            error.detail = detail;
            throw error;
        }
        
        const data = await response.json();
        const decodedData = decodeApiResponse(data);
        
        console.log(`✅ API Request completed: ${url}`);
        return decodedData;
        
    } catch (error) {
        console.error(`❌ API Request failed for ${url}:`, error);
        throw error;
    }
}

/**
 * POST request with automatic encoding/decoding
 * @param {string} url - The URL to post to
 * @param {Object} data - The data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} - The decoded response data
 */
export async function postRequest(url, data, options = {}) {
    return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(data),
        ...options
    });
}

/**
 * GET request with automatic decoding
 * @param {string} url - The URL to get
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} - The decoded response data
 */
export async function getRequest(url, options = {}) {
    return apiRequest(url, {
        method: 'GET',
        ...options
    });
}

// Example usage:
/*
import { postRequest, getRequest, fetchAndDecode } from './utils/responseDecoder';

// Using the enhanced API functions
const propertyData = await postRequest('/api/v1/source1', {
    address: '123 Main St, Melbourne VIC 3000'
});

// Using the basic fetch wrapper
const healthData = await getRequest('/api/v1/health');

// Using the low-level fetch wrapper
const customData = await fetchAndDecode('/api/v1/custom-endpoint', {
    method: 'POST',
    headers: { 'Custom-Header': 'value' },
    body: JSON.stringify({ custom: 'data' })
});
*/
